import { Controller } from 'stimulus';
import toArray from '../helpers/toArray';

const SVG_CLASS = 'sh-growth-path__svg';
const LINE_CLASS = 'sh-growth-path__line';
const STROKE_WIDTH = 5;
const HORIZONTAL_GRAPH_BREAKPOINT = 768;
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

const getBoundingClientRect = (element) => {
  const {
    top,
    right,
    bottom,
    left,
    width,
    height,
    x,
    y,
  } = element.getBoundingClientRect();
  return {
    top,
    right,
    bottom,
    left,
    width,
    height,
    x,
    y,
  };
};

class GrowthPath extends Controller {
  connect() {
    this.removeDuplicateBubbles();
    this.onResize();
  }

  onResize() {
    this.element.classList[this.isHorizontalGraph() ? 'add' : 'remove']('is-horizontal');

    const svgs = toArray(this.element.querySelectorAll(`.${SVG_CLASS}`));

    svgs.forEach((svg) => {
      svg.parentNode.removeChild(svg);
    });

    setTimeout(() => {
      this.drawLines();
    }, 2000);
  }

  onWindowResize() {
    this.onResize();
  }

  drawLines() {
    this.bubbleTargets.forEach((bubble) => {
      const children = this.childrenForBubble(bubble);

      this.connectBubbleToChildren(bubble, children);
    });
  }

  childrenForBubble(bubble) {
    /** @type {HTMLElement|null} */
    const childrenWrapper = bubble.parentNode.querySelector('.js-growth-path-children');

    /** @type {HTMLElement[]} */
    const childrenRowWrappers = toArray(childrenWrapper?.children || []);

    if (!childrenRowWrappers.length) {
      return [];
    }

    // Growth paths can be defined as data-attribute. If not the
    // children will be fetched from the DOM.
    const childrenById = bubble.getAttribute('data-bubble-children');

    if (childrenById.length) {
      // Get the children IDs
      const ids = childrenById.split('|');

      return ids
        .map((id) => {
          return this.element.querySelector(`[data-bubble-id="${id}"]`);
        })
        .filter((el) => el);
    }

    // Fetch children from the DOM
    return childrenRowWrappers
      .map((childrenRowWrapper) => toArray(childrenRowWrapper.children))
      .flat(1)
      .filter((child) => child.className.indexOf('js-growth-path-bubble') >= 0);
  }

  connectBubbleToChildren(bubble, children) {
    const bubbleAnchorPosition = this.isHorizontalGraph() ? 'right center' : 'center bottom';
    const bubbleBounds = this.getAnchorForElement(bubble, bubbleAnchorPosition);

    children.forEach((child) => {
      const childAnchorPosition = this.isHorizontalGraph() ? 'left center' : 'center top';
      const childBounds = this.getAnchorForElement(child, childAnchorPosition);

      const svgElement = this.createSvgElementForConnection(bubbleBounds, childBounds);

      const lineElement = this.createLineElementForConnectionOnSvg(
        svgElement,
        bubbleBounds,
        childBounds,
      );

      svgElement.appendChild(lineElement);

      this.element.appendChild(svgElement);
    });
  }

  /**
   * @param {HTMLElement} element
   * @param {String} position
   * @returns {{x: Number, y: Number}}
   */
  getAnchorForElement(element, position = 'right center') {
    const offsetBounds = this.element.getBoundingClientRect();
    const bubbleOffset = getBoundingClientRect(element);

    // The left bounds are calculated on the window. But if the
    // path is scrolled horizontally, the left position is incorrect.
    bubbleOffset.left += this.element.scrollLeft;

    let x = 0;
    let y = 0;

    if (position === 'right center') {
      x = (bubbleOffset.left - offsetBounds.left) + bubbleOffset.width;
      y = (bubbleOffset.top - offsetBounds.top) + (bubbleOffset.height / 2);
    } else if (position === 'left center') {
      x = bubbleOffset.left - offsetBounds.left;
      y = (bubbleOffset.top - offsetBounds.top) + (bubbleOffset.height / 2);
    } else if (position === 'center top') {
      x = bubbleOffset.left - offsetBounds.left + (bubbleOffset.width / 2);
      y = bubbleOffset.top - offsetBounds.top;
    } else if (position === 'center bottom') {
      x = bubbleOffset.left - offsetBounds.left + (bubbleOffset.width / 2);
      y = (bubbleOffset.top - offsetBounds.top) + bubbleOffset.height;
    }

    return { x, y };
  }

  removeDuplicateBubbles() {
    const seenIds = [];

    this.bubbleTargets.forEach((bubble) => {
      const id = bubble.getAttribute('data-bubble-id');

      if (seenIds.indexOf(id) >= 0) {
        bubble.style.display = 'none';
        return;
      }

      seenIds.push(id);
    });
  }

  /**
   * @param {{x: Number, y: Number}} firstElementBounds
   * @param {{x: Number, y: Number}} secondElementBounds
   * @returns {SVGElement}
   */
  createSvgElementForConnection(firstElementBounds, secondElementBounds) {
    const svgElement = document.createElementNS(SVG_NAMESPACE, 'svg');
    svgElement.style.position = 'absolute';

    // Because a stroke is added we've to fine tune the SVG position to align to the bubbles...
    const subtractTop = this.isHorizontalGraph() ? Math.ceil(STROKE_WIDTH / 2) : 0;
    const subtractLeft = this.isHorizontalGraph() ? 0 : Math.ceil(STROKE_WIDTH / 2);

    // The stroke will cause lines to 'fall-off' the SVG element, so we add a little more
    // room to the SVG...
    const extraWidth = this.isHorizontalGraph() ? 0 : STROKE_WIDTH;
    const extraHeight = this.isHorizontalGraph() ? STROKE_WIDTH : 0;

    const svgPosition = GrowthPath.calculateSvgPosition(firstElementBounds, secondElementBounds);
    svgElement.style.top = `${svgPosition.y - subtractTop}px`;
    svgElement.style.left = `${svgPosition.x - subtractLeft}px`;

    const width = Math.abs(secondElementBounds.x - firstElementBounds.x) + extraWidth;
    const height = Math.abs(secondElementBounds.y - firstElementBounds.y) + extraHeight;

    svgElement.style.width = `${width}px`;
    svgElement.style.height = `${height}px`;
    svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`);
    svgElement.setAttribute('class', SVG_CLASS);

    return svgElement;
  }

  /**
   * @param {SVGElement} svgElement
   * @param {{x: Number, y: Number}} firstElementBounds
   * @param {{x: Number, y: Number}} secondElementBounds
   * @returns {SVGLineElement}
   */
  createLineElementForConnectionOnSvg(svgElement, firstElementBounds, secondElementBounds) {
    /** @type {SVGLineElement} */
    const lineElement = document.createElementNS(SVG_NAMESPACE, 'line');

    const svgPosition = GrowthPath.calculateSvgPosition(firstElementBounds, secondElementBounds);

    const extraXLineSpace = this.isHorizontalGraph() ? 0 : Math.ceil(STROKE_WIDTH / 2);
    const extraYLineSpace = this.isHorizontalGraph() ? Math.ceil(STROKE_WIDTH / 2) : 0;

    lineElement.setAttribute('x1', firstElementBounds.x - svgPosition.x + extraXLineSpace);
    lineElement.setAttribute('x2', secondElementBounds.x - svgPosition.x + extraXLineSpace);

    lineElement.setAttribute('y1', firstElementBounds.y - svgPosition.y + extraYLineSpace);
    lineElement.setAttribute('y2', secondElementBounds.y - svgPosition.y + extraYLineSpace);

    lineElement.setAttribute('stroke', 'currentColor');
    lineElement.setAttribute('stroke-width', '5px');
    lineElement.setAttribute('stroke-dasharray', `0 ${STROKE_WIDTH * 2}`);
    lineElement.setAttribute('stroke-linecap', 'round');

    lineElement.setAttribute('class', LINE_CLASS);

    return lineElement;
  }

  static calculateSvgPosition(firstElementBounds, secondElementBounds) {
    return {
      x: Math.min(secondElementBounds.x, firstElementBounds.x),
      y: Math.min(secondElementBounds.y, firstElementBounds.y),
    };
  }

  isHorizontalGraph() {
    if (this.element.getAttribute('data-force-horizontal')) {
      return true;
    }

    return window.innerWidth >= HORIZONTAL_GRAPH_BREAKPOINT;
  }
}

GrowthPath.targets = [
  'bubble',
];

export default GrowthPath;
